import { createContext, useState, useEffect, type ReactNode } from 'react'
import { authService } from '../services/authService'
import { type User } from '../types'

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  loading: boolean
  login: (email: string, password: string) => Promise<void>
  register: (
    email: string,
    password: string,
    passwordConfirmation: string,
    turnstileToken?: string
  ) => Promise<void>
  logout: () => Promise<void>
  forgotPassword: (email: string) => Promise<void>
  resetPassword: (
    password: string,
    passwordConfirmation: string,
    resetToken: string
  ) => Promise<void>
  refreshUser: () => Promise<void>
  loginWithTokens: (tokens: {
    'access-token': string
    client: string
    uid: string
    expiry?: string
  }) => Promise<void>
  googleLogin: (credential: string) => Promise<void>
}

export const AuthContext = createContext<AuthContextType | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const validateSession = async () => {
      const tokens = authService.getStoredTokens()
      if (tokens) {
        try {
          const userData = await authService.validateToken()
          setUser(userData)
        } catch {
          authService.clearTokens()
        }
      }
      setLoading(false)
    }
    validateSession()
  }, [])

  const login = async (email: string, password: string) => {
    const userData = await authService.login(email, password)
    setUser(userData)
  }

  const register = async (
    email: string,
    password: string,
    passwordConfirmation: string,
    turnstileToken?: string
  ) => {
    const userData = await authService.register(
      email,
      password,
      passwordConfirmation,
      turnstileToken
    )
    setUser(userData)
  }

  const logout = async () => {
    await authService.logout()
    setUser(null)
  }

  const forgotPassword = async (email: string) => {
    await authService.forgotPassword(email)
  }

  const resetPassword = async (
    password: string,
    passwordConfirmation: string,
    resetToken: string
  ) => {
    await authService.resetPassword(password, passwordConfirmation, resetToken)
  }

  const refreshUser = async () => {
    try {
      const userData = await authService.validateToken()
      setUser(userData)
    } catch {
      setUser(null)
    }
  }

  const loginWithTokens = async (tokens: {
    'access-token': string
    client: string
    uid: string
    expiry?: string
  }) => {
    const userData = await authService.loginWithTokens(tokens)
    setUser(userData)
  }

  const googleLogin = async (credential: string) => {
    const userData = await authService.googleLogin(credential)
    setUser(userData)
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        loading,
        login,
        register,
        logout,
        forgotPassword,
        resetPassword,
        refreshUser,
        loginWithTokens,
        googleLogin,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
